<!-- eslint-disable no-unused-vars -->
<script setup>
/* eslint semi: 1 */

// Package imports
import { getCurrentInstance, inject, ref } from 'vue';
import {
  signIn as AmplifySignIn,
  resendSignUpCode as AmplifyResendSignUpCode
} from 'aws-amplify/auth';

// Component imports
import SignUpForm from '@/components/common/sign-in/sign-up-form-tmpl.vue';
import SignUpVerificationForm from '@/components/common/sign-in/sign-up-verification-form-tmpl.vue';

// Mixin imports
import AuthMixin from '@/components/mixins/auth-mixin';
import EventBus from '@/components/mixins/event-bus-mixin';

import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';

// Constants
const SIGN_UP_CONTENTS = {
  FORM: 'form-details',
  VERIFICATION: 'verification'
};

// Injects
const $api = inject('$api');
const $utils = inject('$utils');
const $apiError = inject('$apiError');
const $router = inject('$routerInstance');

// Proxy is the instance of mixins defined in defineOptions
const { proxy } = getCurrentInstance();

// Component Options added so we can get access to mixins inside <script setup>
defineOptions({
  mixins: [AuthMixin]
});

// Props
defineProps({
  isArya: {
    type: Boolean,
    default: true,
  },
  isPageMini: {
    type: Boolean,
    default: false,
  }
});

const emit = defineEmits(['successSubmit', 'redirectToSignIn', 'autoSignInError']);

const cognitoAuthStore = useCognitoAuthStore();

// Variables
const signUpContent = ref(SIGN_UP_CONTENTS.FORM);
const signUpData = ref(null);

// Methods
async function handleSignInUser () {
  if (!signUpData.value?.email || !signUpData.value.password) {
    return;
  }

  const products = $utils.isPageForLicenses()
    ? ['activate']
    : ['arya'];

  $utils.mainLoader(true);

  try {
    const email = signUpData.value.email.toLowerCase();

    await cognitoAuthStore.configureWithCognitoStage(signUpData.value.stage);
    const { nextStep, isSignedIn } = await AmplifySignIn({
      username: email,
      password: signUpData.value.password
    });

    if (isSignedIn && nextStep.signInStep === 'DONE') {
      const amplifySession = await cognitoAuthStore.fetchAndStoreAuthSession();
      // NOTE: we need to enable Arya for both Account and a User
      const preferredUsername = amplifySession.tokens.idToken.payload.preferred_username;

      try {
        await $api.patch(`${CONFIG.API_BASE_URL}/group/products`, { add: products });
        await $api.patch(`${CONFIG.API_BASE_URL}/group/users/${preferredUsername}/products`, { add: products });
        console.log(`Product ${JSON.stringify(products)} enabled. Waiting for the USER_SESSION_UPDATED event after refresh token`);

        $utils.mainLoader(false);
        await proxy.checkTermsOfServices({
          callback: () => {
            $utils.mainLoader(true);
            EventBus.$emit('REFRESH_USER_SESSION');
            // NOTE: We have to wait till session is updated before proceed with remaining requests
            EventBus.$on('USER_SESSION_UPDATED', () => {
              $utils.mainLoader(false);

              proxy.sendSignedInEvent();
              $utils.animate('#signin', 'animated fadeOutLeft');
              $router.push('/loginok', () => {});
            });
          }
        });
      } catch (error) {
        console.log('Error enabling ARYA for either account or the user: ', error);
        $utils.mainLoader(false);
        $apiError.handleAPIRequestError(error.reason, { showDialogNotification: true });
      }
    }
  } catch (error) {
    $utils.mainLoader(false);
    emit('autoSignInError', error);
  }
}

async function onSignUpSuccess (result) {
  signUpData.value = result.data;
  if (result?.autoVerifySignUpSuccessful) {
    await handleSignInUser(signUpData.value);
    return;
  }

  signUpContent.value = SIGN_UP_CONTENTS.VERIFICATION;
}

async function onSignUpVerifySuccess () {
  await handleSignInUser(signUpData.value);
}

async function handleResendVerificationCodeRequest (data) {
  const email = data?.email.toLowerCase();

  if (!email) {
    return;
  }

  $utils.mainLoader(true);

  // Set signup form data
  // Set signup successful to true to show verification code form
  await onSignUpSuccess({
    data: {
      email,
      password: data.password,
      stage: data.stage
    }
  });

  try {
    await cognitoAuthStore.configureWithEmail(email);
    await AmplifyResendSignUpCode({ username: email });
  } catch (error) {
    console.error('Resend verification code error: ', error);
  }

  $utils.mainLoader(false);
}

defineExpose({
  resendVerificationCodeRequest: handleResendVerificationCodeRequest
});
</script>

<template>
  <div
    class="sign-in-card gradient-card default max-[1919px]:p-3! min-[1920px]:px-3! max-[1919px]:pb-5! min-[1920px]:py-6! flex flex-col items-center max-[1919px]:w-[276px]! max-[1919px]:min-h-[478px]! min-[1920px]:w-[356px]! min-[1920px]:min-h-[629px]!"
    :class="{ 'no-background': $utils.isPageForLicenses() }"
  >
    <!-- Title -->
    <h4 class="sign-up-title font-semibold uppercase text-white text-center text-[28px] leading-[100%] py-3 break-words w-full">
      {{ $t('label.signUp') }}
    </h4>

    <sign-up-verification-form
      v-if="signUpContent === SIGN_UP_CONTENTS.VERIFICATION"
      :email="signUpData?.email"
      :stage="signUpData?.stage"
      :is-sign-up-mini="isPageMini"
      @success-submit="onSignUpVerifySuccess"
    />
    <sign-up-form
      v-else
      :is-sign-up-mini="isPageMini"
      @redirect-to-sign-in="$emit('redirectToSignIn')"
      @success-submit="onSignUpSuccess"
    />
  </div>
</template>

<style lang="scss" scoped>
@media (max-width: 1919px) {
  :lang(ru) {
    .sign-up-title {
      font-size: 18px !important;
    }
  }
}

@media (min-width: 1920px) {
  :lang(ru) {
    .sign-up-title {
      font-size: 24px !important;
    }
  }
}
</style>
